/**
 * Two Sense landing — environment & shop constants.
 *
 * Single place to switch which labeau_api the site talks to, mirroring
 * `labeau_dashboard/src/app/utils/constant.js`. Change `LABEAU_ENV` below (or
 * set REACT_APP_LABEAU_ENV / REACT_APP_LABEAU_API_BASE at build time) — nothing
 * else in the codebase should hardcode a host.
 */

/* ── Per-environment API hosts ──
   staging    → local labeau_api on port 8000
   release    → shared pre-production host
   production → live host

   No trailing slash: request paths are written as `${API_BASE_URL}/service/...`
*/
export const ENDPOINTS = {
    staging: "http://localhost:8000", // staging / local
    release: "https://release-api.labeaupos.com", // release
    production: "https://api.labeaupos.com", // production
};

// export const LABEAU_ENV = 'production' // production
// export const LABEAU_ENV = 'release'    // release
export const LABEAU_ENV = process.env.REACT_APP_LABEAU_ENV || "production"; // staging / localhost

/* REACT_APP_LABEAU_API_BASE wins over everything, so a deploy can point at an
   arbitrary host without a code change. Falls back to production if LABEAU_ENV
   is ever set to something unknown. */
export const endpoint_url = process.env.REACT_APP_LABEAU_API_BASE || ENDPOINTS[LABEAU_ENV] || ENDPOINTS.production;

export const API_BASE_URL = endpoint_url;

/* ── Shop identity ── */

// Two Sense's shop id in labeau_api. Every public request is scoped to it.
export const TWO_SENSE_SHOP_ID = 27;

/* Decimal places for the shop's display currency. Two Sense operates in
   Malaysian Ringgit (MYR), which uses 2 decimal places — so prices always show
   them (e.g. "30.00", "1,234.50"). If the shop's currency ever changes, update
   this to match (e.g. a non-decimal currency such as JPY would use 0). */
export const SHOP_CURRENCY_DECIMALS = 2;

export const CURRENCY_LOCALE = "en-MY";

/* ── API tuning ── */

// Page size requested from the DRF-paginated public endpoints.
export const DEFAULT_PAGE_SIZE = 100;

/* ── Site identity (SEO / AEO) ──
   Canonical origin, no trailing slash. EVERY absolute URL on the site derives
   from this — canonical tags, Open Graph, the sitemap and the JSON-LD. Getting
   it wrong risks Google consolidating the pages onto a host you don't own, so
   change it here and nowhere else. */
export const SITE_URL = "https://2sensesalon.com";

/* Name, Address, Phone — the "NAP" that local search engines match against
   Google Business Profile, directories and map listings. It MUST stay
   byte-identical to what is rendered on the Contact section and to the GBP
   listing; inconsistent NAP is the most common cause of weak local ranking.
   Sourced from `contact.location.address` / `contact.businessHours` in
   src/translation/en.json and from TS_WA_NUMBER in src/utils/whatsapp.js. */
export const BUSINESS = {
    name: "Two Sense Hair & Beauty",
    legalName: "Two Sense Hair & Beauty",
    // schema.org type — a beauty salon is a HealthAndBeautyBusiness, which is
    // more specific (and better understood) than a bare LocalBusiness.
    schemaType: "HealthAndBeautyBusiness",
    description: "A considered hair and skin studio in Johor Bahru. Hair and beauty services brought together in one space, by appointment.",
    telephone: "+60 11-6099 3326",
    whatsapp: "https://wa.me/601160993326",
    address: {
        street: "No 49, 49-01 & 49-02, Jalan Jaya Putra 7/9, Bandar Jaya Putra",
        locality: "Johor Bahru",
        region: "Johor",
        postalCode: "81100",
        country: "MY",
    },
    // Malaysian Ringgit; the service menu spans roughly RM60–RM500.
    priceRange: "RM60–RM500",
    currency: "MYR",
    // [opens, closes] in 24h. Mirrors contact.businessHours.
    hours: [
        { days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], opens: "10:00", closes: "20:00" },
        { days: ["Saturday"], opens: "10:00", closes: "19:00" },
        { days: ["Sunday"], opens: "11:00", closes: "18:00" },
    ],
    /* Languages the studio serves in, as BCP-47 tags. Matches the three
       locales shipped in src/translation/. */
    languages: ["en", "ms", "zh"],
    /* TODO: no verified data for these yet — deliberately omitted rather than
       guessed, because wrong values in JSON-LD are worse than absent ones:
         · geo coordinates (latitude/longitude)
         · sameAs[] — Google Business Profile, Instagram, Facebook URLs
       Add them here and they flow into the structured data automatically. */
    sameAs: [],
};

/* Social-share preview image (Open Graph / Twitter). 1200×630 is the size both
   platforms crop to. */
export const OG_IMAGE = "/images/og-cover.jpg";
